/**
 * UasZonesLayer — ŘLP UAS geographic zones on the mission map (GET /api/uas-zones). Lives inside the
 * MapContainer and draws nothing through React: it requests the zones of the current view (with a
 * margin) whenever the map settles outside the area loaded last, and draws them as one canvas GeoJSON layer in its own pane under
 * the flight area. Its state (catalogue, loading, hidden layers, too many zones, error) goes up through
 * `onStatusChange` for UasZonesControl.
 *
 * Height-grid cells are coloured by their permitted height and carry it as a number in the middle, in a
 * pane above the zones, where the cell is large enough on screen.
 *
 * Only the zones valid on `date` (the mission date; default today) are shown. When `interactive` is
 * false (drawing, placing the take-off point) the zones ignore clicks so they cannot steal them.
 * `isClickSuppressed` (optional, asked on each click) drops a click that still belongs to a drawing
 * which has just ended — its zones popup would close the popup the drawing opened.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import api from '../../lib/api';
import {
  buildZoneQuery,
  gridCellLabel,
  gridLabelFontPx,
  gridLabelHtml,
  isTooManyZonesError,
  isViewCoveredByQuery,
  layerCodesForThemes,
  toDateParam,
  zonesAtPoint,
  zonesPopupHtml,
  zoneStyle,
} from '../../lib/uasZones';

const PANE = 'uasZonesPane';
/** Below the overlay pane (400) where the flight area and markers are drawn. */
const PANE_Z_INDEX = 350;
const LABEL_PANE = 'uasZonesLabelPane';
/** Over the zones, still under the flight area; the numbers never take clicks. */
const LABEL_PANE_Z_INDEX = 360;
/** Where a cell number must fit, it is sized for the longest value, so all cells at one zoom match. */
const GRID_LABEL_SIZING_TEXT = '120';
/** Wait for the map to settle before asking — a drag or wheel zoom fires many moveends. */
export const UAS_ZONES_DEBOUNCE_MS = 300;

const catalogueCache = new Map();

/** GET /api/uas-zones/layers for a date, shared by every map on the page. */
const fetchCatalogue = (day) => {
  const key = day || 'today';
  if (!catalogueCache.has(key)) {
    const request = api.get('/uas-zones/layers', { params: day ? { date: day } : {} })
      .then((response) => response.data)
      .catch((error) => {
        catalogueCache.delete(key);
        throw error;
      });
    catalogueCache.set(key, request);
  }
  return catalogueCache.get(key);
};

/** Test hook: forget the cached catalogues. */
export const clearUasZoneCatalogueCache = () => catalogueCache.clear();

const readView = (map) => {
  const bounds = map.getBounds();
  return {
    south: bounds.getSouth(),
    west: bounds.getWest(),
    north: bounds.getNorth(),
    east: bounds.getEast(),
    zoom: map.getZoom(),
  };
};

export const UasZonesLayer = ({
  enabledThemes,
  date = null,
  language = 'cs',
  labels = {},
  interactive = true,
  isClickSuppressed,
  onStatusChange,
}) => {
  const map = useMap();
  const day = toDateParam(date);
  const [catalogue, setCatalogue] = useState(null);
  const [catalogueError, setCatalogueError] = useState(false);
  const [view, setView] = useState(() => readView(map));
  const [collection, setCollection] = useState(null);
  const [requestState, setRequestState] = useState({ loading: false, hiddenLayers: [], tooMany: false, error: false });

  const statusRef = useRef(onStatusChange);
  statusRef.current = onStatusChange;
  // Read on each click, so a new function every render does not rebuild the zones layer.
  const clickSuppressedRef = useRef(isClickSuppressed);
  clickSuppressedRef.current = isClickSuppressed;
  // Query of the zones on the map now; a view still inside its bbox needs no new request.
  const loadedQueryRef = useRef(null);

  // Follow the map: settle-debounced view after pans, zooms and container resizes.
  useEffect(() => {
    let timer = null;
    const onViewChange = () => {
      clearTimeout(timer);
      timer = setTimeout(() => setView(readView(map)), UAS_ZONES_DEBOUNCE_MS);
    };
    map.on('moveend resize', onViewChange);
    return () => {
      clearTimeout(timer);
      map.off('moveend resize', onViewChange);
    };
  }, [map]);

  useEffect(() => {
    let cancelled = false;
    setCatalogue(null);
    setCatalogueError(false);
    fetchCatalogue(day)
      .then((data) => { if (!cancelled) setCatalogue(data); })
      .catch(() => { if (!cancelled) setCatalogueError(true); });
    return () => { cancelled = true; };
  }, [day]);

  const layersByCode = useMemo(
    () => new Map((catalogue?.layers || []).map((layer) => [layer.code, layer])),
    [catalogue],
  );
  const layerCodes = useMemo(
    () => layerCodesForThemes(catalogue?.layers, enabledThemes),
    [catalogue, enabledThemes],
  );
  const layerKey = layerCodes.join(',');

  useEffect(() => {
    if (!catalogue) return undefined;
    const query = buildZoneQuery({ ...view, layerCodes, date: day });
    if (!query) {
      loadedQueryRef.current = null;
      setCollection(null);
      setRequestState({ loading: false, hiddenLayers: [], tooMany: false, error: false });
      return undefined;
    }
    if (isViewCoveredByQuery(loadedQueryRef.current, query, view)) {
      // A request for another view may have been cancelled on the way here.
      setRequestState((prev) => (prev.loading ? { ...prev, loading: false } : prev));
      return undefined;
    }

    const controller = new AbortController();
    setRequestState((prev) => ({ ...prev, loading: true }));
    api.get('/uas-zones', { params: query, signal: controller.signal })
      .then((response) => {
        // An answer already on its way when the view changed must not replace the newer one.
        if (controller.signal.aborted) return;
        loadedQueryRef.current = query;
        setCollection(response.data);
        setRequestState({ loading: false, hiddenLayers: response.data?.hidden_layers || [], tooMany: false, error: false });
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        loadedQueryRef.current = null;
        setCollection(null);
        setRequestState({ loading: false, hiddenLayers: [], tooMany: isTooManyZonesError(error), error: !isTooManyZonesError(error) });
      });
    return () => controller.abort();
    // layerKey stands for layerCodes (a new array on every catalogue/theme change).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogue, layerKey, view, day]);

  useEffect(() => {
    statusRef.current?.({
      catalogue,
      catalogueError,
      enabledLayerCount: layerCodes.length,
      count: collection?.features?.length ?? 0,
      ...requestState,
    });
  }, [catalogue, catalogueError, layerCodes.length, collection, requestState]);

  const renderer = useMemo(() => {
    if (!map.getPane(PANE)) {
      map.createPane(PANE).style.zIndex = String(PANE_Z_INDEX);
    }
    return L.canvas({ pane: PANE, padding: 0.5 });
  }, [map]);

  useEffect(() => {
    if (!collection?.features?.length) return undefined;
    const layer = L.geoJSON(collection, {
      pane: PANE,
      renderer,
      interactive,
      attribution: labels.attribution,
      style: (feature) => zoneStyle(layersByCode.get(feature.properties?.layer), feature.properties),
    }).addTo(map);

    // The canvas reports only the topmost zone under the cursor; the popup lists every zone at the point.
    const showZonesAt = (event) => {
      if (clickSuppressedRef.current?.()) return;
      const { lat, lng } = event.latlng;
      const found = zonesAtPoint(collection.features, lng, lat);
      const features = found.length ? found : [event.layer?.feature].filter(Boolean);
      const entries = features.map((feature) => ({ layer: layersByCode.get(feature.properties?.layer), properties: feature.properties }));
      L.popup({ maxWidth: 320 }).setLatLng(event.latlng).setContent(zonesPopupHtml(entries, language, labels)).openOn(map);
    };
    if (interactive) layer.on('click', showZonesAt);

    return () => {
      layer.off('click', showZonesAt);
      map.removeLayer(layer);
    };
  }, [map, renderer, collection, layersByCode, interactive, language, labels]);

  // Height-grid cells show their permitted height in the middle; left out where it does not fit.
  const zoom = view.zoom;
  useEffect(() => {
    if (!collection?.features?.length) return undefined;
    if (!map.getPane(LABEL_PANE)) {
      const pane = map.createPane(LABEL_PANE);
      pane.style.zIndex = String(LABEL_PANE_Z_INDEX);
      pane.style.pointerEvents = 'none';
    }

    const markers = [];
    collection.features.forEach((feature) => {
      if (!layersByCode.get(feature.properties?.layer)?.is_height_grid) return;
      const label = gridCellLabel(feature);
      if (!label) return;
      const [west, south, east, north] = label.bounds;
      const southWest = map.project([south, west], zoom);
      const northEast = map.project([north, east], zoom);
      const fontPx = gridLabelFontPx(Math.abs(northEast.x - southWest.x), Math.abs(southWest.y - northEast.y),
        GRID_LABEL_SIZING_TEXT.length, zoom);
      if (!fontPx) return;
      markers.push(L.marker([label.lat, label.lng], {
        pane: LABEL_PANE,
        interactive: false,
        keyboard: false,
        // A 0 × 0 icon sits exactly on the point; the number centres itself on it.
        icon: L.divIcon({ className: 'uas-zone-grid-label', iconSize: [0, 0], html: gridLabelHtml(label, fontPx) }),
      }));
    });
    if (!markers.length) return undefined;

    const group = L.layerGroup(markers).addTo(map);
    return () => {
      map.removeLayer(group);
    };
  }, [map, collection, layersByCode, zoom]);

  return null;
};

export default UasZonesLayer;
